/**
 * HourSelect — designed dropdown for picking one of a fixed list of hours
 * (e.g. the clinic's calendar start hour). Follows the 2026 "consequential
 * action dropdown" pattern (see `pd2-status-select`): labeled full-width
 * trigger with a rotating caret → absolute listbox menu with a Check on the
 * active row, outside-click / touchstart / Escape close, keyboard navigation.
 *
 * Usage:
 *   <HourSelect
 *     value={5}
 *     options={CALENDAR_START_HOUR_OPTIONS}   // [{ value: number, label: string }]
 *     onChange={(hour) => …}
 *     ariaLabel="שעת פתיחת היומן"
 *   />
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { CaretDown, Check, Clock } from "@phosphor-icons/react";
import "./HourSelect.css";

export default function HourSelect({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel,
  id,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);

  const selected = options.find((o) => o.value === value) || null;

  const close = useCallback(() => setOpen(false), []);

  // Outside click / touch closes the menu
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
    };
  }, [open, close]);

  // Scroll active option into view when opening
  useEffect(() => {
    if (!open) return;
    const active = menuRef.current?.querySelector(".hour-select-option.active");
    active?.scrollIntoView?.({ block: "nearest" });
    active?.focus?.({ preventScroll: true });
  }, [open]);

  const commit = (nextValue) => {
    close();
    triggerRef.current?.focus?.({ preventScroll: true });
    if (nextValue !== value) onChange?.(nextValue);
  };

  const handleTriggerKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
    }
    if (e.key === "Escape" && open) {
      e.preventDefault();
      close();
    }
  };

  const handleMenuKeyDown = (e) => {
    const items = Array.from(
      menuRef.current?.querySelectorAll(".hour-select-option") || []
    );
    const idx = items.indexOf(document.activeElement);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      items[Math.min(items.length - 1, idx + 1)]?.focus();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      items[Math.max(0, idx - 1)]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === "Escape" || e.key === "Tab") {
      close();
      if (e.key === "Escape") {
        e.preventDefault();
        triggerRef.current?.focus?.({ preventScroll: true });
      }
    }
  };

  return (
    <div
      className={`hour-select ${open ? "open" : ""} ${disabled ? "disabled" : ""} ${className}`}
      ref={rootRef}
    >
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className={`hour-select-trigger ${open ? "open" : ""}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={handleTriggerKeyDown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        <span className="hour-select-trigger-value">
          <span className="hour-select-trigger-icon" aria-hidden="true">
            <Clock size={14} weight="fill" />
          </span>
          <span className="hour-select-trigger-text" dir="ltr">
            {selected ? selected.label : "—"}
          </span>
        </span>
        <CaretDown
          size={14}
          weight="bold"
          className={`hour-select-caret ${open ? "open" : ""}`}
        />
      </button>

      {open && (
        <div
          className="hour-select-menu"
          role="listbox"
          ref={menuRef}
          onKeyDown={handleMenuKeyDown}
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            const isActive = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isActive}
                tabIndex={-1}
                className={`hour-select-option ${isActive ? "active" : ""}`}
                onClick={() => commit(option.value)}
              >
                <span className="hour-select-option-label" dir="ltr">
                  {option.label}
                </span>
                {isActive && <Check size={14} weight="bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
